/**
 * Typed client for the Otto IQ API (CONTRACT §6).
 *
 * Three things this module is responsible for:
 *
 *  1. One place that knows the URL shapes. Screens never build a path.
 *  2. One place that knows the wire shapes. Domain types come from
 *     `@otto-iq/shared`; the wire form is `Serialized<T>` (Dates as ISO
 *     strings). Request/response envelopes that the contract does not spell
 *     out are declared here and are marked as such.
 *  3. The live-trace SSE reader for `GET /runs/:id/events`, with its own
 *     reconnect + `Last-Event-ID` resume, because a run that takes four
 *     minutes must survive a laptop lid closing.
 *
 * List responses are read through `toList()`, which accepts either a bare
 * array or `{ items: [...] }`. The API is being written in parallel; being
 * tolerant of both costs nothing and removes a coordination round trip.
 */

import type {
  Agent,
  Approval,
  Artifact,
  AuditEntry,
  Channel,
  GateExpression,
  Incident,
  IncidentStatus,
  Integration,
  Invoice,
  JsonObject,
  JsonValue,
  Memory,
  Person,
  Release,
  RiskClass,
  Role,
  Run,
  RunEventKind,
  RunStatus,
  Schedule,
  Serialized,
  Session,
  Skill,
  SkillStatus,
  SkillVersion,
  Tenant,
  TenantStatus,
  ToolGrant,
  Trigger,
  TriggerKind,
  UsageRecord,
} from '@otto-iq/shared';

/* ------------------------------------------------------------------------- *
 * Wire types
 * ------------------------------------------------------------------------- */

export type AgentDTO = Serialized<Agent>;
export type ApprovalDTO = Serialized<Approval>;
export type ArtifactDTO = Serialized<Artifact>;
export type AuditEntryDTO = Serialized<AuditEntry>;
export type IncidentDTO = Serialized<Incident>;
export type IntegrationDTO = Serialized<Integration>;
export type InvoiceDTO = Serialized<Invoice>;
export type MemoryDTO = Serialized<Memory>;
export type PersonDTO = Serialized<Person>;
export type ReleaseDTO = Serialized<Release>;
export type RunDTO = Serialized<Run>;
export type ScheduleDTO = Serialized<Schedule>;
export type SessionDTO = Serialized<Session>;
export type SkillDTO = Serialized<Skill>;
export type SkillVersionDTO = Serialized<SkillVersion>;
export type TenantDTO = Serialized<Tenant>;
export type ToolGrantDTO = Serialized<ToolGrant>;
export type TriggerDTO = Serialized<Trigger>;
export type UsageRecordDTO = Serialized<UsageRecord>;

/**
 * A `run_events` row as it arrives over SSE. `tenant_id` is deliberately
 * optional: the API has no reason to echo the tenant back to a tenant-scoped
 * client, but nothing breaks if it does.
 */
export interface RunEventDTO {
  id: string;
  runId: string;
  tenantId?: string;
  seq: number;
  kind: RunEventKind;
  payload: JsonObject;
  createdAt: string;
}

/**
 * `GET /runs/:id` and `POST /runs/:id/stop` both answer with the run alongside
 * its approvals/artifacts/usage rather than a bare run row (confirmed live —
 * apps/api/src/routes/runs.ts). `getRun`/`stopRun` were typed `Promise<RunDTO>`
 * and returned this wrapper unchanged, so every field read off the result was
 * `undefined`; the one page that called `getRun` before this fix worked around
 * it locally rather than trip over a shared-file edit mid-batch.
 */
export interface RunDetailDTO {
  run: RunDTO;
  approvals: ApprovalDTO[];
  artifacts: ArtifactDTO[];
  usage: UsageRecordDTO[];
  eventCount: number;
}

/** `GET /me` — who is signed in, where, and as what. */
export interface MeResponse {
  person: PersonDTO;
  role: Role;
  tenant: TenantDTO | null;
  tenants: TenantDTO[];
  /** True when the signed-in principal holds the Otto staff role claim. */
  isOttoStaff?: boolean;
  /** Present and active when this session was minted by `POST /master/operator/:tenantId`. */
  operator?: OperatorContext | null;
}

export interface OperatorContext {
  active: boolean;
  /** The Otto staff member behind the operator session. */
  operatorName?: string;
  operatorEmail?: string;
  expiresAt?: string;
}

/** `GET /tenants/:id/overview`. Counters are optional — a screen renders `—` for anything absent. */
export interface OverviewStats {
  tasksToday?: number;
  tasksThisWeek?: number;
  minutesSaved?: number;
  pendingApprovals?: number;
  activeSkills?: number;
  failedRuns?: number;
  spendTodayMicroUsd?: number;
  dailyBudgetCents?: number;
}

export interface OverviewResponse {
  stats: OverviewStats;
  pendingApprovals: ApprovalDTO[];
  recentRuns: RunDTO[];
  recentSessions?: SessionDTO[];
}

/** `POST /chat`. */
export interface ChatRequest {
  agentId: string;
  sessionId?: string;
  text: string;
}

export interface ChatResponse {
  runId: string;
  sessionId: string;
}

/**
 * `GET /sessions/:id/messages` — the read-only transcript. Modelled as a
 * flattened run event: the API may put `role`/`text` at the top level or leave
 * them inside `payload`; `transcriptText()` reads both.
 */
export interface SessionMessageDTO {
  id: string;
  runId: string | null;
  seq: number;
  kind: RunEventKind;
  role?: 'user' | 'assistant' | 'system';
  text?: string;
  payload?: JsonObject;
  createdAt: string;
}

/** `POST /sessions/:id/fork` — see `api.forkSession` for the full contract note. */
export interface ForkSessionResponse {
  sessionId: string;
  sandbox: true;
  sourceSessionId: string;
  session: SessionDTO;
}

export interface ApprovalDecisionRequest {
  decision: 'approve' | 'reject';
  note?: string;
  channel: Channel;
}

/** A tool grant flattened for the Integrations screen. */
export interface ToolGrantSummary {
  toolId: string;
  name: string;
  description: string;
  risk: 'read' | 'write' | 'destructive';
  enabled: boolean;
  locked: boolean;
  ottoLocked?: boolean;
}

/**
 * A minimal JSON Schema, as far as the connect form is concerned.
 *
 * Not a full validator — the API validates against the connector's real schema.
 * This is only enough to render a field per property and mark the required ones,
 * so the console never has to know anything vendor-specific.
 */
export interface ConnectFieldSchema {
  type?: string;
  required?: string[];
  properties?: Record<
    string,
    { type?: string; description?: string; enum?: string[]; maxLength?: number }
  >;
}

export interface ConnectorSummary {
  id: string;
  name: string;
  description: string;
  /**
   * Registered so the framework can exercise it, never offered to a client as
   * something to connect. The Integrations page builds its "not connected yet"
   * list from this catalogue, so without this a fixture connector would be
   * advertised to every tenant.
   */
  internal?: boolean;
  auth: {
    kind: string;
    scopes?: string[];
    /** Non-secret setup stored on the integration row. */
    configSchema?: ConnectFieldSchema | null;
    /**
     * The secret half. Present only for connectors whose credential is typed in
     * rather than minted by an OAuth callback — so its absence is what tells the
     * form to offer an authorisation flow instead of a password box.
     */
    credentialSchema?: ConnectFieldSchema | null;
  };
  tools: Array<{
    id: string;
    name: string;
    description: string;
    risk: 'read' | 'write' | 'destructive';
    ottoLocked?: boolean;
    defaultEnabled?: boolean;
  }>;
}

export interface UsageSummary {
  periodStart?: string;
  periodEnd?: string;
  tasks?: number;
  tasksIncluded?: number;
  minutesSaved?: number;
  costMicroUsd?: number;
  byModel?: Array<{ model: string; tasks: number; costMicroUsd: number }>;
  bySkill?: Array<{ skillId: string; name: string; tasks: number; minutesSaved: number }>;
  daily?: Array<{ date: string; tasks: number; costMicroUsd: number }>;
  records?: UsageRecordDTO[];
}

export interface UtilisationDailyPoint {
  day: string;
  costMicroUsd: number;
  inputTokens: number;
  outputTokens: number;
}

export interface UtilisationModelBreakdown {
  model: string;
  calls: number;
  costMicroUsd: number;
  inputTokens: number;
  outputTokens: number;
}

export interface UtilisationAgentBreakdown {
  agentId: string;
  name: string;
  costMicroUsd: number;
}

export interface UtilisationSummary {
  period: { from: string; to: string };
  budget: {
    dailyBudgetCents: number;
    spentTodayCents: number;
    remainingCents: number;
    percentUsed: number;
  };
  daily: UtilisationDailyPoint[];
  byModel: UtilisationModelBreakdown[];
  byAgent: UtilisationAgentBreakdown[];
}

/** Counts keyed by whatever status/severity values are actually present — never a fixed set. */
export type StatusTally = Record<string, number>;

/**
 * The `attention` row shape from `GET /master/fleet`. Corrected 9 Aug 2026:
 * this previously `extends IncidentDTO`, which promises `partialState`,
 * `acknowledgedAt`, `resolvedAt`, `createdAt` and `updatedAt` — the route's
 * `select({...})` for this queue (apps/api/src/routes/master.ts) never
 * projects those columns, so every consumer of the full `IncidentDTO` shape
 * here would have read `undefined` typed as present. This lists exactly the
 * columns the route selects, plus the server-joined `tenantName`.
 */
export interface FleetAttentionItem {
  id: string;
  tenantId: string;
  /** Joined server-side so the queue doesn't need a second lookup per row. */
  tenantName: string;
  agentId: string | null;
  runId: string | null;
  code: string;
  title: string;
  severity: IncidentDTO['severity'];
  status: IncidentDTO['status'];
  openedAt: string;
  clientNarrative: string | null;
}

/**
 * `GET /master/fleet`. Corrected 9 Aug 2026 against the live API — the previous
 * version of this type declared flat numbers; the route actually returns
 * tallies keyed by status/severity (e.g. `{ agents: { paused: 1, active: 2 } }`),
 * discovered and worked around locally in fleet.tsx before this fix landed.
 */
export interface FleetSummary {
  tenants: StatusTally;
  agents: StatusTally;
  runsToday: StatusTally;
  pendingApprovals: number;
  openIncidents: StatusTally;
  attention: FleetAttentionItem[];
}

/**
 * `GET /master/releases` joins each release with its tenant pins server-side
 * (apps/api/src/routes/master.ts); `ReleaseDTO` alone is typed from the bare
 * `releases` row and doesn't carry that. `pins` is a route-specific join, not
 * a domain field, so it lives here rather than widening `Release` in
 * `@otto-iq/shared` — a pin is not a property of a release, it's a fact about
 * one tenant's relationship to it.
 */
export interface ReleasePinDTO {
  id: string;
  tenantId: string;
  tenantName: string;
  releaseId: string;
  pinnedAt: string;
}

export interface ReleaseWithPinsDTO extends ReleaseDTO {
  pins?: ReleasePinDTO[];
}

/**
 * `GET /master/billing`. Corrected 9 Aug 2026 — real shape is `{ period, orgs:
 * [...] }`, one row per tenant with margin computed server-side; the previous
 * `{ invoices, totals }` shape didn't exist on the wire, so `.then()` read
 * `data.invoices` off `undefined` and every call silently resolved to an
 * empty list — a real runtime bug, not just a stale type (discovered and
 * worked around with a hand-rolled fetch in billing.tsx before this fix).
 */
export interface BillingOrgRow {
  tenantId: string;
  name: string;
  slug: string;
  plan: string;
  tasks: number;
  modelCostMicroUsd: number;
  modelCostCents: number;
  planFeeCents: number;
  marginCents: number;
  invoiceStatus: string | null;
}

export interface BillingSummary {
  period: string;
  orgs: BillingOrgRow[];
}

/** `GET/PATCH /master/settings/alerts/:id` — Otto-owned, no tenant. */
export type AlertSeverity = 'P1' | 'P2' | 'P3' | 'P4';

export interface AlertRouteDTO {
  id: string;
  event: string;
  severity: AlertSeverity;
  route: string;
  createdAt: string;
  updatedAt: string;
}

/* ------------------------------------------------------------------------- *
 * Automations — holds and triggers (Automations page); rules (Guardrails page)
 * ------------------------------------------------------------------------- */

/**
 * `GET /triggers` joins in the firing URL. `null` for a mailbox, which has
 * none, and also `null` for a ConnectWise callback, whose URL carries its
 * secret in the path and is therefore only ever returned by create and rotate.
 */
export type TriggerWithUrlDTO = TriggerDTO & { webhookUrl: string | null };

/**
 * `POST /triggers` and `POST /triggers/:id/secret` are the only two responses in
 * this API that carry secret material. The value is shown once and never
 * retrievable again — the server keeps only the reference.
 */
export interface TriggerCreatedDTO {
  trigger: TriggerWithUrlDTO;
  /** `null` for a mailbox, and for a listed ConnectWise callback. */
  signingSecret: string | null;
}

/** Mirrors `HoldReason` in `packages/mcp-iq/src/scoring.ts` / `apps/api/src/routes/automations.ts`. */
export type HoldReason = 'no_candidates' | 'below_threshold' | 'ambiguous';

export interface HoldRankedCandidateDTO {
  skillId: string;
  name: string;
  score: number;
  matchedTerms: string[];
  missingTerms: string[];
}

/**
 * `GET /automations/holds` — a hold is not a row in any table, it's a
 * derivation over `run_events` (see `apps/api/src/routes/automations.ts`'s
 * module doc). There is deliberately no id, status or "resolved" field: the
 * only stable key for one hold is `runId` + `callId` together.
 */
export interface HoldQueueItemDTO {
  runId: string;
  sessionId: string;
  agentId: string;
  channel: Channel;
  runStatus: RunStatus;
  runTitle: string | null;
  occurredAt: string;
  callId: string;
  holdReason: HoldReason;
  ask: string | null;
  threshold: number;
  candidatesConsidered: number;
  requestExcerpt: string;
  toolHints: string[];
  ranked: HoldRankedCandidateDTO[];
}

/**
 * `GET /rules` row shape (`apps/api/src/routes/rules.ts`'s `toRuleView`) — the
 * raw `policy_rules` DB row plus a server-computed `setBy`. Deliberately not
 * `@otto-iq/shared`'s `PolicyRule` (the merge-engine's shape: `toolMatch`,
 * `ottoLocked`, `riskMatch[]`) — the wire row uses different field names
 * (`toolId`, `locked`, singular `risk`) because it mirrors the DB column
 * layout, not the engine's in-memory shape. Confirmed against a live
 * `curl .../rules`.
 */
export interface RuleDTO {
  id: string;
  tenantId: string;
  agentId: string | null;
  name: string;
  description: string | null;
  toolId: string | null;
  risk: RiskClass | null;
  gate: GateExpression;
  requiredRole: Role | null;
  locked: boolean;
  enabled: boolean;
  priority: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  setBy: 'otto' | 'tenant';
}

export interface CreateRuleRequest {
  agentId?: string | null;
  name: string;
  description?: string;
  toolId?: string | null;
  risk?: RiskClass;
  gate: GateExpression;
  requiredRole?: Role;
  enabled?: boolean;
  priority?: number;
}

export interface UpdateRuleRequest {
  agentId?: string | null;
  name?: string;
  description?: string | null;
  toolId?: string | null;
  risk?: RiskClass | null;
  gate?: GateExpression;
  requiredRole?: Role | null;
  enabled?: boolean;
  priority?: number;
}

export interface CreateTriggerRequest {
  agentId: string;
  kind: TriggerKind;
  /** The only required field. The API derives the slug and mints the URL. */
  name: string;
  /** Optional: what the agent should do when a delivery arrives. */
  instruction?: string | null;
  config?: JsonObject;
  enabled?: boolean;
}

export interface UpdateTriggerRequest {
  agentId?: string;
  name?: string;
  instruction?: string | null;
  config?: JsonObject;
  enabled?: boolean;
}

/* ------------------------------------------------------------------------- *
 * Transport
 * ------------------------------------------------------------------------- */

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/**
 * The API's real error body (`apps/api/src/errors.ts`'s `ApiError.toBody()`):
 * `{ error: { code, message, details? } }` — always nested. This was
 * previously typed flat (`{ error?: string; message?: string; code?: string }`),
 * which matches no route in this codebase; every 4xx/5xx response actually
 * uses `registerErrorHandler`'s single shape. Confirmed live — `curl -X PATCH
 * .../rules/:id` with a loosening edit returns
 * `{"error":{"code":"conflict","message":"...","details":{"loosened":[...]}}}`.
 * With the old flat type, `body.message` was `undefined` and `body.error` was
 * this whole object, so `ApiError`'s `message` (built-in `Error` coerces a
 * non-string constructor argument with `ToString`) became the useless
 * `"[object Object]"`, and `.code`/`.details` were always `undefined` — no
 * caller ever saw the real message or the `loosened` dimension a 409 names.
 * Same species of wire/DTO drift as the five wrapped-response mismatches
 * documented elsewhere in this file, just on the error path instead of the
 * success path, which is why nothing caught it: no page read `.details`
 * before the rules screen needed to render "you can tighten it, never loosen
 * it" verbatim.
 */
export interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: JsonValue;
  };
  /** Fastify's own 404 handler and schema-validation failures use this shape too. */
  message?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly details: JsonValue | undefined;
  readonly url: string;

  constructor(message: string, init: { status: number; url: string; body?: ApiErrorBody }) {
    super(message);
    this.name = 'ApiError';
    this.status = init.status;
    this.url = init.url;
    this.code = init.body?.error?.code;
    this.details = init.body?.error?.details;
  }

  /** 401/403 mean "sign in again" or "you may not"; everything else is a fault. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

type TokenProvider = () => string | null | Promise<string | null>;

let tokenProvider: TokenProvider | null = null;

/**
 * Installed by the auth layer once MSAL is wired. Until then the API relies on
 * the session cookie, which is what `credentials: 'include'` is for.
 */
export function setAuthTokenProvider(provider: TokenProvider | null): void {
  tokenProvider = provider;
}

/**
 * Operator-mode bearer token — held in memory only, for the lifetime of an
 * Otto staff member's visit inside one client's console. Minted by
 * `api.master.operatorToken(tenantId)`, this is what `request()` attaches
 * as `Authorization: Bearer <token>` so the API's auth plugin (see
 * `apps/api/src/auth/plugin.ts` — `looksLikeOperatorToken` / `resolveOperator`)
 * resolves the caller as that operator rather than the signed-in session
 * cookie. It never touches storage, so a reload or a closed tab drops it —
 * operator mode is re-entered from `/master`, not resumed silently.
 */
interface OperatorTokenState {
  token: string;
  tenantId: string;
  expiresAt: string;
}

let operatorTokenState: OperatorTokenState | null = null;

export function setOperatorToken(token: string, tenantId: string, expiresAt: string): void {
  operatorTokenState = { token, tenantId, expiresAt };
}

export function clearOperatorToken(): void {
  operatorTokenState = null;
}

export function getOperatorToken(): OperatorTokenState | null {
  return operatorTokenState;
}

/**
 * Exported because `integrations.tsx` issues two calls with its own `fetch`
 * (see the comment on `rawApiRequest` there for why) and must authenticate the
 * same way `request()` does.
 *
 * It did not, and could not: it sent `credentials: 'include'` and no
 * `Authorization` header, but nothing in this API authenticates by cookie. The
 * whole page 401'd — reported to the user as "your session has expired" — for
 * anybody signed in with Entra or acting in operator mode.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  // Operator mode wins over the normal session — an Otto staff member acting
  // as an operator inside a client tenant must be attributed as the operator,
  // not fall back to whatever the signed-in cookie session would resolve to.
  if (operatorTokenState) return { authorization: `Bearer ${operatorTokenState.token}` };
  if (!tokenProvider) return {};
  const token = await tokenProvider();
  return token ? { authorization: `Bearer ${token}` } : {};
}

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined | Array<string | number>
>;

function buildUrl(path: string, params?: QueryParams): string {
  const url = `${API_BASE_URL}${path}`;
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  params?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
  /** Accept header override — used by the CSV export routes. */
  accept?: string;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, options.params);
  const method = options.method ?? 'GET';

  const headers: Record<string, string> = {
    accept: options.accept ?? 'application/json',
    ...(await authHeaders()),
  };
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      credentials: 'include',
      signal: options.signal,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError('Could not reach the Otto IQ API.', { status: 0, url });
  }

  if (!response.ok) {
    let body: ApiErrorBody | undefined;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      body = undefined;
    }
    // The real shape is `{ error: { message } }` (see `ApiErrorBody` above);
    // `body?.message` only covers Fastify's own not-found/validation replies,
    // which don't nest under `error`.
    const message =
      body?.error?.message ?? body?.message ?? `${response.status} ${response.statusText}`.trim();
    throw new ApiError(message, { status: response.status, url, body });
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return (await response.text()) as T;
  return (await response.json()) as T;
}

/**
 * Normalise a list response to an array.
 *
 * The API wraps collections in a *named* envelope — `{ agents: [...] }`,
 * `{ approvals: [...] }`, `{ connectors: [...] }` — rather than a uniform
 * `items`. Accepting only `items` here meant every list in the console
 * silently resolved to `[]`: the agent switcher reported "no agent has been
 * provisioned" while the API was returning two. Silent, because an empty list
 * is a legitimate answer, so nothing threw.
 *
 * Accepted, in order: a bare array; `{ items: [...] }`; an object with exactly
 * one array-valued property (the named envelope). Anything else yields `[]`.
 * The single-property rule is deliberate — it will not guess when a payload
 * carries two arrays.
 */
export function toList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (!payload || typeof payload !== 'object') return [];

  const record = payload as Record<string, unknown>;
  if (Array.isArray(record['items'])) return record['items'] as T[];

  const arrayKeys = Object.keys(record).filter((key) => Array.isArray(record[key]));
  if (arrayKeys.length === 1) return record[arrayKeys[0] as string] as T[];

  return [];
}

async function getList<T>(path: string, params?: QueryParams, signal?: AbortSignal): Promise<T[]> {
  return toList<T>(await request<unknown>(path, { params, signal }));
}

/* ------------------------------------------------------------------------- *
 * Client console routes
 * ------------------------------------------------------------------------- */

/**
 * The drilldown rows. Neither is a `Serialized<Agent>` or `Serialized<Person>`:
 * both carry counts the route computes and neither carries the heavy fields
 * (persona prompt, for one) that a list has no use for.
 */
export interface MasterOrgAgent {
  id: string;
  name: string;
  slug: string;
  roleLabel: string;
  status: string;
  /**
   * Otto's override pause. Separate from `status` because `status` is the
   * client's own control — see `agents.otto_paused_at`.
   */
  ottoPausedAt: string | null;
  ottoPauseReason: string | null;
  ottoPausedBy: string | null;
  model: string;
  dailyBudgetCents: number | null;
  guardrailReleaseId: string | null;
  createdAt: string;
  updatedAt: string;
  runs: number;
  lastRunAt: string | null;
}

export interface MasterOrgPerson {
  id: string;
  name: string;
  email: string;
  role: Role;
  status: string;
  canApprove: boolean;
  canTeach: boolean;
  approvalLimitCents: number;
  createdAt: string;
  /** Rows in `identities` — zero means invited but never actually signed in. */
  linkedIdentities: number;
}

export const api = {
  me(signal?: AbortSignal): Promise<MeResponse> {
    return request<MeResponse>('/me', { signal });
  },

  overview(tenantId: string, signal?: AbortSignal): Promise<OverviewResponse> {
    return request<OverviewResponse>(`/tenants/${encodeURIComponent(tenantId)}/overview`, {
      signal,
    }).then((data) => ({
      stats: data?.stats ?? {},
      pendingApprovals: toList<ApprovalDTO>(data?.pendingApprovals),
      recentRuns: toList<RunDTO>(data?.recentRuns),
      recentSessions: toList<SessionDTO>(data?.recentSessions),
    }));
  },

  /* --- agents --- */
  listAgents(signal?: AbortSignal): Promise<AgentDTO[]> {
    return getList<AgentDTO>('/agents', undefined, signal);
  },
  /**
   * Archived agents are hidden from `GET /agents`, because as far as the client
   * is concerned an archived agent has been removed. The flag is only honoured
   * for an operator or Otto staff — anyone else gets the ordinary list back
   * rather than an error, since asking is not an offence.
   */
  listArchivedAgents(signal?: AbortSignal): Promise<AgentDTO[]> {
    return getList<AgentDTO>('/agents', { includeArchived: true }, signal).then((list) =>
      list.filter((agent) => agent.status === 'archived'),
    );
  },
  /**
   * `POST /agents/:id/archive` — how an agent is removed from a tenancy.
   *
   * Not a delete: the agent's runs, approvals, artifacts and audit trail stay
   * exactly where they are. Otto's to call, from operator mode or the master
   * surface, and it lands in the client's own audit log as `agent.archived`.
   */
  archiveAgent(id: string, reason?: string): Promise<AgentDTO> {
    return request<{ agent: AgentDTO }>(`/agents/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
      body: reason === undefined ? {} : { reason },
    }).then((data) => data.agent);
  },
  /** `POST /agents/:id/reinstate` — comes back **paused**, never straight to active. */
  reinstateAgent(id: string): Promise<AgentDTO> {
    return request<{ agent: AgentDTO }>(`/agents/${encodeURIComponent(id)}/reinstate`, {
      method: 'POST',
      body: {},
    }).then((data) => data.agent);
  },
  createAgent(body: Partial<AgentDTO> & { name: string }): Promise<AgentDTO> {
    return request<AgentDTO>('/agents', { method: 'POST', body });
  },
  updateAgent(id: string, body: Partial<AgentDTO>): Promise<AgentDTO> {
    // Wrapped as { agent } (confirmed live — `curl -X PATCH .../agents/:id`),
    // not a bare AgentDTO. Was typed and returned bare, so agent-settings.tsx's
    // onSuccess handlers read `updated.id`/`updated.name` off `undefined`: the
    // agents-cache patch in `applyUpdate` never matched by id (silently a
    // no-op until the follow-up invalidate refetched), and the prompt-save
    // toast showed "undefined's layer two takes effect...". Same species of
    // bug as the five wrap-vs-bare mismatches already fixed elsewhere in this
    // file (getRun, updateMemory/forgetMemory, invitePerson/updatePerson,
    // listReleases, setToolEnabled).
    return request<{ agent: AgentDTO }>(`/agents/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
    }).then((data) => data.agent);
  },

  /* --- chat + runs --- */
  chat(body: ChatRequest): Promise<ChatResponse> {
    return request<ChatResponse>('/chat', { method: 'POST', body });
  },
  listRuns(
    params?: { agentId?: string; sessionId?: string; status?: RunStatus; limit?: number },
    signal?: AbortSignal,
  ): Promise<RunDTO[]> {
    return getList<RunDTO>('/runs', params, signal);
  },
  getRun(id: string, signal?: AbortSignal): Promise<RunDetailDTO> {
    return request<RunDetailDTO>(`/runs/${encodeURIComponent(id)}`, { signal });
  },
  stopRun(id: string): Promise<RunDTO> {
    return request<{ run: RunDTO }>(`/runs/${encodeURIComponent(id)}/stop`, {
      method: 'POST',
    }).then((data) => data.run);
  },
  /** Non-streaming read of a run's events — used to seed the trace before subscribing. */
  listRunEvents(id: string, afterSeq?: number, signal?: AbortSignal): Promise<RunEventDTO[]> {
    return getList<RunEventDTO>(
      `/runs/${encodeURIComponent(id)}/events`,
      { after: afterSeq, format: 'json' },
      signal,
    );
  },

  /* --- sessions --- */
  listSessions(
    params?: { agentId?: string; channel?: Channel; limit?: number },
    signal?: AbortSignal,
  ): Promise<SessionDTO[]> {
    return getList<SessionDTO>('/sessions', params, signal);
  },
  /**
   * `GET /sessions/current?agentId=` — the session the next message would
   * continue, or null.
   *
   * Server-resolved by scope key, not looked up in the session list: the list
   * is tenant-wide, so filtering it here would risk showing one person another
   * person's chat, and in operator mode there is no `personId` to filter on.
   */
  currentSession(agentId: string, signal?: AbortSignal): Promise<SessionDTO | null> {
    return request<{ session: SessionDTO | null }>('/sessions/current', {
      params: { agentId },
      signal,
    }).then((data) => data?.session ?? null);
  },
  /**
   * `POST /sessions/:id/end` — finish a conversation.
   *
   * Nothing is deleted: the thread keeps its runs and transcript and stays in
   * the sessions list. It stops being the live session for its scope key, so
   * the next message starts a new one.
   */
  endSession(id: string): Promise<SessionDTO> {
    return request<{ session: SessionDTO }>(`/sessions/${encodeURIComponent(id)}/end`, {
      method: 'POST',
      body: {},
    }).then((data) => data.session);
  },
  sessionMessages(id: string, signal?: AbortSignal): Promise<SessionMessageDTO[]> {
    // Not a plain list envelope: the response also carries `runs` alongside
    // `messages`, so the generic single-array-key heuristic in `toList()`
    // sees two array keys and returns nothing. Read the named field directly.
    return request<{ messages?: unknown }>(`/sessions/${encodeURIComponent(id)}/messages`, {
      signal,
    }).then((data) => toList<SessionMessageDTO>(data?.messages));
  },
  /**
   * `POST /sessions/:id/fork` — Test Bench's entry point. Confirmed live
   * (`apps/api/src/routes/sessions.ts`): replies 201 with `{ sessionId,
   * sandbox: true, sourceSessionId, session }`, not a bare `SessionDTO`. The
   * forked session shares the source's `scopeKey` (memories carry over
   * unchanged) but starts a fresh SDK conversation (`sdkSessionId: null`) —
   * see the route's own inline comments before assuming more isolation than
   * that. Runs against it withhold every connector that is not
   * `sandboxSafe`, structurally, at toolset-assembly time.
   */
  forkSession(sessionId: string): Promise<ForkSessionResponse> {
    return request<ForkSessionResponse>(`/sessions/${encodeURIComponent(sessionId)}/fork`, {
      method: 'POST',
      body: {},
    });
  },

  /* --- approvals --- */
  listApprovals(
    params?: { status?: 'pending' | 'history'; agentId?: string; limit?: number },
    signal?: AbortSignal,
  ): Promise<ApprovalDTO[]> {
    return getList<ApprovalDTO>('/approvals', params, signal);
  },
  decideApproval(id: string, body: ApprovalDecisionRequest): Promise<ApprovalDTO> {
    return request<ApprovalDTO>(`/approvals/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body,
    });
  },

  /* --- skills --- */
  listSkills(
    params?: { agentId?: string; status?: SkillStatus },
    signal?: AbortSignal,
  ): Promise<SkillDTO[]> {
    return getList<SkillDTO>('/skills', params, signal);
  },
  createSkill(body: Record<string, unknown>): Promise<SkillDTO> {
    return request<SkillDTO>('/skills', { method: 'POST', body });
  },
  updateSkill(id: string, body: Record<string, unknown>): Promise<SkillDTO> {
    return request<SkillDTO>(`/skills/${encodeURIComponent(id)}`, { method: 'PATCH', body });
  },
  decideSkill(
    id: string,
    // Matches the API's enum exactly (apps/api/src/routes/skills.ts) — 'activate' here
    // 400s. Caught while briefing the Skills-page batch, before anyone built against it.
    body: { decision: 'approve' | 'decline' | 'retire'; note?: string },
  ): Promise<SkillDTO> {
    return request<SkillDTO>(`/skills/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body,
    });
  },

  /* --- memories --- */
  listMemories(
    params?: { agentId?: string; scopeKey?: string; pinned?: boolean },
    signal?: AbortSignal,
  ): Promise<MemoryDTO[]> {
    return getList<MemoryDTO>('/memories', params, signal);
  },
  updateMemory(id: string, body: { pinned?: boolean; fact?: string }): Promise<MemoryDTO> {
    // Wrapped as { memory } (confirmed live) — was typed and returned bare, so
    // every caller read `undefined` off a successful save.
    return request<{ memory: MemoryDTO }>(`/memories/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
    }).then((data) => data.memory);
  },
  forgetMemory(id: string): Promise<void> {
    // Wrapped as { forgotten: id } (confirmed live), not an empty body.
    return request<{ forgotten: string }>(`/memories/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }).then(() => undefined);
  },

  /* --- integrations --- */
  listIntegrations(params?: { agentId?: string }, signal?: AbortSignal): Promise<IntegrationDTO[]> {
    // ?agentId scopes tools[]/grants[] to one agent (confirmed live — without
    // it, tool_grants for every agent on the tenant are indistinguishable).
    return getList<IntegrationDTO>('/integrations', params, signal);
  },
  /**
   * `POST /integrations` — this tenant's row for a connector, created if it is
   * the first time anyone here has started connecting it.
   *
   * Idempotent, so calling it on a retry (or from a second tab) returns the
   * same row rather than a second one. It has to happen before the credential
   * call, because the row's id is what names the secret in the vault.
   */
  createIntegration(connectorId: string): Promise<IntegrationDTO> {
    return request<{ integration: IntegrationDTO }>('/integrations', {
      method: 'POST',
      body: { connectorId },
    }).then((data) => data.integration);
  },
  /** `DELETE /integrations/:id` — abandon a connect, removing its credential too. */
  cancelIntegration(id: string): Promise<void> {
    return request<{ cancelled: boolean }>(`/integrations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }).then(() => undefined);
  },
  connectIntegration(id: string, body?: Record<string, unknown>): Promise<IntegrationDTO> {
    return request<{ integration: IntegrationDTO }>(
      `/integrations/${encodeURIComponent(id)}/connect`,
      { method: 'POST', body: body ?? {} },
    ).then((data) => data.integration);
  },
  disconnectIntegration(id: string): Promise<IntegrationDTO> {
    return request<{
      integration: IntegrationDTO;
      held: { toolGrants: number; tools: string[]; note: string };
    }>(`/integrations/${encodeURIComponent(id)}/disconnect`, { method: 'POST', body: {} }).then(
      (data) => data.integration,
    );
  },
  testIntegration(id: string): Promise<{ ok: boolean; message?: string }> {
    return request<{ ok: boolean; message?: string }>(
      `/integrations/${encodeURIComponent(id)}/test`,
      { method: 'POST', body: {} },
    );
  },
  setToolEnabled(
    integrationId: string,
    toolId: string,
    agentId: string,
    enabled: boolean,
  ): Promise<ToolGrantDTO> {
    // agentId is required — tool_grants is unique on (agent_id, tool_id), and
    // the route 400s without it (confirmed live).
    return request<{ grant: ToolGrantDTO }>(
      `/integrations/${encodeURIComponent(integrationId)}/tools/${encodeURIComponent(toolId)}`,
      { method: 'PATCH', body: { agentId, enabled } },
    ).then((data) => data.grant);
  },
  listConnectors(signal?: AbortSignal): Promise<ConnectorSummary[]> {
    return getList<ConnectorSummary>('/connectors', undefined, signal);
  },
  /**
   * `PUT /integrations/:id/credentials` — the one route that takes credential
   * *values*.
   *
   * They go straight to the secret store; what comes back is the integration
   * with a reference on it. Nothing here is echoed, persisted on the row or
   * written to the audit ledger, so this resolved value never contains what was
   * sent.
   */
  /**
   * `POST /integrations/:id/authorize` — begin an OAuth flow.
   *
   * Returns a URL to send the browser to, rather than redirecting here: the
   * consent screen has to be a top-level navigation the person can see and
   * trust, and a fetch cannot produce one.
   */
  startIntegrationAuthorization(id: string): Promise<{ url: string }> {
    return request<{ url: string }>(`/integrations/${encodeURIComponent(id)}/authorize`, {
      method: 'POST',
    });
  },
  setIntegrationCredentials(
    id: string,
    body: { secret: Record<string, string>; config?: Record<string, unknown> },
  ): Promise<IntegrationDTO> {
    return request<{ integration: IntegrationDTO }>(
      `/integrations/${encodeURIComponent(id)}/credentials`,
      { method: 'PUT', body },
    ).then((data) => data.integration);
  },
  /**
   * `POST /agents/:agentId/teams-bot` — provisions (or re-provisions, from an
   * `attention` row) one Teams-bot integration row scoped to this agent.
   * Route was not live at the time this was written: `curl -X POST
   * .../agents/agt_riley/teams-bot` on 9 Aug 2026 returned `{"error":
   * {"code":"not_found","message":"Route POST /api/v1/agents/agt_riley/
   * teams-bot not found."}}`, meaning the backend batch for this had not
   * landed yet. Built to the agreed contract — wrapped as `{ integration }`,
   * matching every other integration-mutating route in this file
   * (`connectIntegration`/`disconnectIntegration` above) — rather than against
   * a running server. Re-check the response shape with `curl` once that batch
   * lands.
   */
  createTeamsBot(agentId: string, displayName: string): Promise<IntegrationDTO> {
    return request<{ integration: IntegrationDTO }>(
      `/agents/${encodeURIComponent(agentId)}/teams-bot`,
      { method: 'POST', body: { displayName } },
    ).then((data) => data.integration);
  },
  /** `DELETE /agents/:agentId/teams-bot` — see `createTeamsBot` above for the same not-yet-live caveat. */
  deleteTeamsBot(agentId: string): Promise<{ deleted: string }> {
    return request<{ deleted: string }>(`/agents/${encodeURIComponent(agentId)}/teams-bot`, {
      method: 'DELETE',
    });
  },

  /* --- audit --- */
  listAudit(
    params?: { from?: string; to?: string; type?: string; limit?: number },
    signal?: AbortSignal,
  ): Promise<AuditEntryDTO[]> {
    return getList<AuditEntryDTO>('/audit', params, signal);
  },
  auditExportUrl(params?: { from?: string; to?: string; type?: string }): string {
    return buildUrl('/audit/export.csv', params);
  },

  /* --- people --- */
  listPeople(signal?: AbortSignal): Promise<PersonDTO[]> {
    return getList<PersonDTO>('/people', undefined, signal);
  },
  addPerson(body: { name: string; email: string; role: Role }): Promise<PersonDTO> {
    // Wrapped as { person } (confirmed live) — was typed and returned bare.
    return request<{ person: PersonDTO }>('/people', { method: 'POST', body }).then(
      (data) => data.person,
    );
  },
  updatePerson(id: string, body: Partial<PersonDTO>): Promise<PersonDTO> {
    return request<{ person: PersonDTO }>(`/people/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
    }).then((data) => data.person);
  },

  /* --- usage, artifacts, schedules --- */
  usage(
    params?: { agentId?: string; period?: string },
    signal?: AbortSignal,
  ): Promise<UsageSummary> {
    return request<UsageSummary>('/usage', { params, signal });
  },
  utilisation(
    params?: { agentId?: string; period?: string },
    signal?: AbortSignal,
  ): Promise<UtilisationSummary> {
    return request<UtilisationSummary>('/utilisation', { params, signal });
  },
  listArtifacts(params?: { agentId?: string; runId?: string }, signal?: AbortSignal) {
    return getList<ArtifactDTO>('/artifacts', params, signal);
  },
  artifactUrl(id: string): Promise<{ url: string; expiresAt?: string }> {
    return request<{ url: string; expiresAt?: string }>(`/artifacts/${encodeURIComponent(id)}/url`);
  },
  listSchedules(params?: { agentId?: string }, signal?: AbortSignal): Promise<ScheduleDTO[]> {
    return getList<ScheduleDTO>('/schedules', params, signal);
  },
  updateSchedule(id: string, body: { enabled?: boolean; cron?: string }): Promise<ScheduleDTO> {
    // Wrapped as { schedule } (confirmed live — `curl -X PATCH .../schedules/:id`),
    // not a bare ScheduleDTO. Was typed and returned bare — the sixth instance
    // of the same wrap-vs-bare drift as `getRun`/`updateMemory`/`forgetMemory`/
    // `invitePerson`/`updatePerson`/`listReleases`/`setToolEnabled` elsewhere in
    // this file, found here while building the Automations page's Schedules tab.
    return request<{ schedule: ScheduleDTO }>(`/schedules/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
    }).then((data) => data.schedule);
  },
  runSchedule(id: string): Promise<{ runId: string }> {
    return request<{ runId: string }>(`/schedules/${encodeURIComponent(id)}/run`, {
      method: 'POST',
      body: {},
    });
  },

  /* --- triggers (Automations: incoming events) --- */
  listTriggers(
    params?: { agentId?: string; kind?: TriggerKind; enabled?: boolean },
    signal?: AbortSignal,
  ): Promise<TriggerWithUrlDTO[]> {
    return getList<TriggerWithUrlDTO>('/triggers', params, signal);
  },
  /** Returns the whole envelope, not just the row — the secret is only here. */
  createTrigger(body: CreateTriggerRequest): Promise<TriggerCreatedDTO> {
    return request<TriggerCreatedDTO>('/triggers', { method: 'POST', body });
  },
  /**
   * Returns the new URL as well as the secret, and the URL is the part that
   * matters for a ConnectWise callback: its secret lives *in* the path, so
   * rotating one changes where ConnectWise must post. Showing the secret alone
   * would leave the caller with a working credential and a dead URL.
   */
  rotateTriggerSecret(id: string): Promise<{ signingSecret: string; webhookUrl: string | null }> {
    return request<{ signingSecret: string; webhookUrl: string | null }>(
      `/triggers/${encodeURIComponent(id)}/secret`,
      { method: 'POST', body: {} },
    );
  },
  updateTrigger(id: string, body: UpdateTriggerRequest): Promise<TriggerWithUrlDTO> {
    return request<{ trigger: TriggerWithUrlDTO }>(`/triggers/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
    }).then((data) => data.trigger);
  },
  deleteTrigger(id: string): Promise<void> {
    return request<{ deleted: string }>(`/triggers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }).then(() => undefined);
  },

  /* --- automations: the unrecognised-event (hold) queue --- */
  listHolds(
    params?: {
      agentId?: string;
      channel?: Channel;
      holdReason?: HoldReason;
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
    },
    signal?: AbortSignal,
  ): Promise<HoldQueueItemDTO[]> {
    // Named envelope is `{ holds, total, limit, offset }` — not a plain list
    // (confirmed live), so this reads the field directly rather than going
    // through `getList`/`toList`'s single-array-key heuristic, which would
    // work here too but this is more explicit about the pagination fields
    // being dropped on the floor for now (no console pagination UI yet).
    return request<{ holds?: unknown }>('/automations/holds', { params, signal }).then((data) =>
      toList<HoldQueueItemDTO>(data?.holds),
    );
  },

  /* --- rules (Guardrails: thresholds and always-ask categories) --- */
  listRules(
    params?: { agentId?: string; toolId?: string; locked?: boolean; enabled?: boolean },
    signal?: AbortSignal,
  ): Promise<RuleDTO[]> {
    return getList<RuleDTO>('/rules', params, signal);
  },
  createRule(body: CreateRuleRequest): Promise<RuleDTO> {
    return request<{ rule: RuleDTO }>('/rules', { method: 'POST', body }).then((data) => data.rule);
  },
  updateRule(id: string, body: UpdateRuleRequest): Promise<RuleDTO> {
    return request<{ rule: RuleDTO }>(`/rules/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
    }).then((data) => data.rule);
  },
  deleteRule(id: string): Promise<void> {
    return request<{ deleted: string }>(`/rules/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }).then(() => undefined);
  },

  /* --- master console --- */
  master: {
    fleet(signal?: AbortSignal): Promise<FleetSummary> {
      return request<FleetSummary>('/master/fleet', { signal });
    },
    listOrgs(signal?: AbortSignal): Promise<TenantDTO[]> {
      return getList<TenantDTO>('/master/orgs', undefined, signal);
    },
    /**
     * `POST /master/orgs`. Corrected 9 Aug 2026 against the route source
     * (apps/api/src/routes/master.ts) — it replies 201 with `{ tenant, owner }`,
     * never a bare `TenantDTO`. Same species of wire/DTO drift as the five
     * wrapped-response mismatches already fixed elsewhere in this file
     * (see COMPLETION-PLAN.md §"bug 3"). orgs.tsx already worked around it —
     * its `onSuccess` toasts off the submitted form rather than the resolved
     * value — so this was a silent type lie rather than a live break.
     */
    createOrg(body: {
      name: string;
      slug: string;
      plan: string;
      // Both were always required by the route and always sent by the page —
      // the type simply never said so, which is the other half of the same lie.
      ownerName: string;
      ownerEmail: string;
      /** A domain or a directory guid; the API resolves either. */
      entraTenant?: string;
    }): Promise<{ tenant: TenantDTO; owner: PersonDTO }> {
      return request('/master/orgs', { method: 'POST', body });
    },
    /**
     * `GET /master/orgs/:id/agents` and `…/people` — the drilldowns behind the
     * counts on the Orgs & agents table. Read-only: anything Otto changes
     * inside a tenant goes through operator mode, which the client's own audit
     * log attributes to `otto:operator:<ref>`.
     */
    /**
     * `POST /master/agents/:id/pause` — Otto's override hold.
     *
     * The agent stops running and its settings freeze for the client, but every
     * run, session, approval and audit row stays readable. Idempotent.
     */
    pauseAgent(id: string, reason?: string): Promise<MasterOrgAgent> {
      return request<{ agent: MasterOrgAgent }>(`/master/agents/${encodeURIComponent(id)}/pause`, {
        method: 'POST',
        body: reason === undefined ? {} : { reason },
      }).then((data) => data.agent);
    },
    /**
     * `POST /master/agents/:id/resume`. Clears only the override — the client's
     * own `status` was never touched, so they get back the agent they had.
     */
    resumeAgent(id: string): Promise<MasterOrgAgent> {
      return request<{ agent: MasterOrgAgent }>(`/master/agents/${encodeURIComponent(id)}/resume`, {
        method: 'POST',
        body: {},
      }).then((data) => data.agent);
    },
    orgAgents(id: string, signal?: AbortSignal): Promise<MasterOrgAgent[]> {
      return request<{ agents: MasterOrgAgent[] }>(
        `/master/orgs/${encodeURIComponent(id)}/agents`,
        { signal },
      ).then((data) => toList<MasterOrgAgent>(data?.agents));
    },
    orgPeople(id: string, signal?: AbortSignal): Promise<MasterOrgPerson[]> {
      return request<{ people: MasterOrgPerson[] }>(
        `/master/orgs/${encodeURIComponent(id)}/people`,
        { signal },
      ).then((data) => toList<MasterOrgPerson>(data?.people));
    },
    /**
     * `PATCH /master/orgs/:id` — the write half of tenant lifecycle.
     *
     * `status` is the consequential one: `authPlugin` 403s every request for a
     * suspended workspace, and the runner and scheduler both refuse to do work
     * for one. Sending the state a tenant is already in is an idempotent
     * no-op, not an error.
     */
    /**
     * `PATCH /master/orgs/:id`. `pauseReason` is stored only when `status`
     * becomes `paused`, and cleared when it leaves — it is what the client is
     * shown on every refusal while the tenancy is held.
     */
    updateOrg(
      id: string,
      body: {
        status?: TenantStatus;
        plan?: string;
        pauseReason?: string;
        /** A domain or a guid to map, or `null` to clear the mapping. */
        entraTenant?: string | null;
      },
    ): Promise<{ tenant: TenantDTO }> {
      return request(`/master/orgs/${encodeURIComponent(id)}`, { method: 'PATCH', body });
    },
    /**
     * `POST /master/agents`. Corrected 9 Aug 2026 — the route replies 201 with
     * `{ agent }`, not a bare `AgentDTO`. See `createOrg` above for the same
     * pattern; `orgs.tsx`'s `NewAgentModal` has the same defensive `onSuccess`.
     */
    createAgent(body: Record<string, unknown>): Promise<{ agent: AgentDTO }> {
      return request('/master/agents', { method: 'POST', body });
    },
    operatorToken(tenantId: string): Promise<{ token: string; expiresAt: string }> {
      return request<{ token: string; expiresAt: string }>(
        `/master/operator/${encodeURIComponent(tenantId)}`,
        { method: 'POST', body: {} },
      );
    },
    listIncidents(params?: { status?: string }, signal?: AbortSignal): Promise<IncidentDTO[]> {
      return getList<IncidentDTO>('/master/incidents', params, signal);
    },
    /**
     * `POST /master/incidents/:id/retry`. Corrected 9 Aug 2026 against the
     * route source (apps/api/src/routes/master.ts) — it does not return the
     * updated `IncidentDTO`, it returns `{ incidentId, runId, status }`. No
     * caller currently reads the resolved value, so this was a silent type
     * lie rather than a live break, but the next caller that does read it
     * would have gotten a compile-time green light for fields that don't
     * exist on the wire.
     */
    retryIncident(id: string): Promise<{ incidentId: string; runId: string; status: 'queued' }> {
      return request(`/master/incidents/${encodeURIComponent(id)}/retry`, {
        method: 'POST',
        body: {},
      });
    },
    /**
     * `PATCH /master/incidents/:id` — acknowledge, resolve or reopen.
     *
     * Distinct from `retryIncident`, which re-enqueues the run *and* moves the
     * incident to `acknowledged` as a side effect. This one only moves the
     * incident, which is what closing something out actually needs.
     */
    updateIncident(
      id: string,
      body: { status: IncidentStatus; note?: string },
    ): Promise<{ incident: IncidentDTO }> {
      return request(`/master/incidents/${encodeURIComponent(id)}`, { method: 'PATCH', body });
    },
    billing(params?: { period?: string }, signal?: AbortSignal): Promise<BillingSummary> {
      return request<BillingSummary>('/master/billing', { params, signal });
    },
    listReleases(signal?: AbortSignal): Promise<ReleaseWithPinsDTO[]> {
      return getList<ReleaseWithPinsDTO>('/master/releases', undefined, signal);
    },
    /**
     * `POST /master/releases/:id/pin`. Corrected 9 Aug 2026 against the route
     * source — it returns 201 with `{ pin, release }`, never `{ ok: boolean }`
     * (that shape doesn't exist anywhere on this endpoint). `pin` is the raw
     * `tenant_pins` row (no joined `tenantName`), which is why it isn't typed
     * as `ReleasePinDTO`. No caller reads the resolved value today.
     */
    pinRelease(
      id: string,
      tenantId: string,
    ): Promise<{
      pin: {
        id: string;
        tenantId: string;
        releaseId: string;
        pinnedAt: string;
        pinnedBy: string | null;
      };
      release: { id: string; kind: string; version: string };
    }> {
      return request(`/master/releases/${encodeURIComponent(id)}/pin`, {
        method: 'POST',
        body: { tenantId },
      });
    },
    settings: {
      listAlertRoutes(signal?: AbortSignal): Promise<AlertRouteDTO[]> {
        return request<{ alertRoutes: AlertRouteDTO[] }>('/master/settings/alerts', {
          signal,
        }).then((data) => toList<AlertRouteDTO>(data?.alertRoutes));
      },
      updateAlertRoute(
        id: string,
        body: { severity?: AlertSeverity; route?: string },
      ): Promise<AlertRouteDTO> {
        return request<{ alertRoute: AlertRouteDTO }>(
          `/master/settings/alerts/${encodeURIComponent(id)}`,
          { method: 'PATCH', body },
        ).then((data) => data.alertRoute);
      },
    },
  },
} as const;

/* ------------------------------------------------------------------------- *
 * Live run trace — Server-Sent Events
 * ------------------------------------------------------------------------- */

/**
 * Why not `EventSource`? Because it cannot carry an `Authorization` header and
 * cannot be told to resume from a specific id on the *first* connection — both
 * of which we need (a bearer token once MSAL lands, and resuming a trace the
 * user has already partly seen). So: `fetch` + a small SSE parser + our own
 * backoff. `Last-Event-ID` is sent exactly as the spec requires, so a
 * standards-compliant server needs no special casing.
 */
export interface SseMessage {
  id?: string;
  event: string;
  data: string;
  retry?: number;
}

export type RunStreamMessage =
  | { type: 'open' }
  | { type: 'event'; event: RunEventDTO }
  | { type: 'status'; status: RunStatus }
  | { type: 'reconnecting'; attempt: number; delayMs: number }
  | { type: 'closed'; reason: 'complete' | 'aborted' | 'error'; error?: unknown };

export interface RunStreamOptions {
  /** Resume point. The server echoes it as the `id:` field of each event. */
  lastEventId?: string | null;
  onMessage: (message: RunStreamMessage) => void;
  /** Give up after this many consecutive failed connections. Default: unlimited. */
  maxRetries?: number;
  /** Injectable for tests. */
  fetchImpl?: typeof fetch;
}

export interface RunStreamHandle {
  close(): void;
  /** The highest event id seen so far — the resume cursor. */
  readonly lastEventId: string | null;
}

const SSE_BACKOFF_MIN_MS = 500;
const SSE_BACKOFF_MAX_MS = 8_000;

function backoffDelay(attempt: number, serverRetryMs: number | null): number {
  if (serverRetryMs && serverRetryMs > 0) return Math.min(serverRetryMs, SSE_BACKOFF_MAX_MS);
  const exponential = Math.min(SSE_BACKOFF_MIN_MS * 2 ** (attempt - 1), SSE_BACKOFF_MAX_MS);
  // Full jitter — many consoles watching one incident must not reconnect in lockstep.
  return Math.round(exponential * (0.5 + Math.random() * 0.5));
}

/**
 * Parses a chunk of an SSE body, dispatching each complete message. Returns the
 * unconsumed tail, which the caller feeds back in with the next chunk.
 */
export function parseSseChunk(buffer: string, emit: (message: SseMessage) => void): string {
  const normalised = buffer.replace(/\r\n|\r/g, '\n');
  let rest = normalised;

  for (;;) {
    const boundary = rest.indexOf('\n\n');
    if (boundary === -1) return rest;

    const block = rest.slice(0, boundary);
    rest = rest.slice(boundary + 2);

    let id: string | undefined;
    let event = 'message';
    let retry: number | undefined;
    const dataLines: string[] = [];

    for (const rawLine of block.split('\n')) {
      if (rawLine === '' || rawLine.startsWith(':')) continue;
      const colon = rawLine.indexOf(':');
      const field = colon === -1 ? rawLine : rawLine.slice(0, colon);
      let value = colon === -1 ? '' : rawLine.slice(colon + 1);
      if (value.startsWith(' ')) value = value.slice(1);

      switch (field) {
        case 'id':
          id = value;
          break;
        case 'event':
          event = value || 'message';
          break;
        case 'data':
          dataLines.push(value);
          break;
        case 'retry': {
          const parsed = Number.parseInt(value, 10);
          if (Number.isFinite(parsed)) retry = parsed;
          break;
        }
        default:
          break;
      }
    }

    if (dataLines.length > 0 || id !== undefined || retry !== undefined) {
      emit({ id, event, data: dataLines.join('\n'), retry });
    }
  }
}

const TERMINAL_RUN_STATUSES: ReadonlySet<string> = new Set(['done', 'failed', 'stopped']);

/**
 * Subscribes to `GET /runs/:id/events`. Reconnects with jittered backoff and
 * resumes from the last id it saw. Returns a handle; call `close()` on unmount.
 */
export function openRunEventStream(runId: string, options: RunStreamOptions): RunStreamHandle {
  const controller = new AbortController();
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const url = buildUrl(`/runs/${encodeURIComponent(runId)}/events`);

  let lastEventId: string | null = options.lastEventId ?? null;
  let serverRetryMs: number | null = null;
  let closed = false;

  const emit = (message: RunStreamMessage) => {
    if (!closed || message.type === 'closed') options.onMessage(message);
  };

  const handleMessage = (message: SseMessage): 'continue' | 'complete' => {
    if (message.id) lastEventId = message.id;
    if (message.retry) serverRetryMs = message.retry;

    if (message.event === 'ping' || message.event === 'keepalive') return 'continue';
    if (message.event === 'done' || message.event === 'end') return 'complete';

    if (!message.data) return 'continue';

    let parsed: unknown;
    try {
      parsed = JSON.parse(message.data);
    } catch {
      return 'continue';
    }

    if (message.event === 'status' || message.event === 'run.status') {
      const status = (parsed as { status?: RunStatus })?.status;
      if (status) {
        emit({ type: 'status', status });
        if (TERMINAL_RUN_STATUSES.has(status)) return 'complete';
      }
      return 'continue';
    }

    const record = parsed as Partial<RunEventDTO> & { status?: RunStatus };
    if (record && typeof record.kind === 'string') {
      emit({
        type: 'event',
        event: {
          id: record.id ?? message.id ?? `${runId}:${record.seq ?? 0}`,
          runId: record.runId ?? runId,
          seq: typeof record.seq === 'number' ? record.seq : Number(message.id ?? 0),
          kind: record.kind,
          payload: (record.payload ?? {}) as JsonObject,
          createdAt: record.createdAt ?? new Date().toISOString(),
        },
      });
      return 'continue';
    }

    if (record?.status) {
      emit({ type: 'status', status: record.status });
      if (TERMINAL_RUN_STATUSES.has(record.status)) return 'complete';
    }
    return 'continue';
  };

  const run = async () => {
    let attempt = 0;

    while (!closed) {
      try {
        const headers: Record<string, string> = {
          accept: 'text/event-stream',
          'cache-control': 'no-cache',
          ...(await authHeaders()),
        };
        if (lastEventId) headers['last-event-id'] = lastEventId;

        const response = await doFetch(url, {
          headers,
          credentials: 'include',
          signal: controller.signal,
        });

        if (!response.ok) {
          // 4xx other than 408/429 is not going to fix itself.
          if (
            response.status >= 400 &&
            response.status < 500 &&
            ![408, 429].includes(response.status)
          ) {
            emit({
              type: 'closed',
              reason: 'error',
              error: new ApiError(`Live trace unavailable (${response.status}).`, {
                status: response.status,
                url,
              }),
            });
            return;
          }
          throw new ApiError(`Live trace failed (${response.status}).`, {
            status: response.status,
            url,
          });
        }
        if (!response.body) throw new ApiError('Live trace returned no body.', { status: 0, url });

        attempt = 0;
        emit({ type: 'open' });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let complete = false;

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          buffer = parseSseChunk(buffer, (message) => {
            if (handleMessage(message) === 'complete') complete = true;
          });
          if (complete) break;
        }

        await reader.cancel().catch(() => undefined);

        if (complete) {
          emit({ type: 'closed', reason: 'complete' });
          return;
        }
        if (closed) return;
        // Clean EOF without a terminal event: the server rotated us. Reconnect.
      } catch (error) {
        if (closed || controller.signal.aborted) {
          emit({ type: 'closed', reason: 'aborted' });
          return;
        }
        attempt += 1;
        if (options.maxRetries !== undefined && attempt > options.maxRetries) {
          emit({ type: 'closed', reason: 'error', error });
          return;
        }
      }

      if (closed) return;
      attempt = Math.max(attempt, 1);
      const delayMs = backoffDelay(attempt, serverRetryMs);
      emit({ type: 'reconnecting', attempt, delayMs });
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  };

  void run();

  return {
    close() {
      if (closed) return;
      closed = true;
      controller.abort();
    },
    get lastEventId() {
      return lastEventId;
    },
  };
}

/* ------------------------------------------------------------------------- *
 * Payload readers
 * ------------------------------------------------------------------------- */

/** Pulls display text out of a run event / transcript row, wherever the API put it. */
export function transcriptText(row: SessionMessageDTO | RunEventDTO): string {
  const direct = (row as SessionMessageDTO).text;
  if (typeof direct === 'string' && direct.length > 0) return direct;
  const payload = row.payload as Record<string, unknown> | undefined;
  const text = payload?.['text'];
  if (typeof text === 'string') return text;
  const summary = payload?.['summary'];
  if (typeof summary === 'string') return summary;
  const reason = payload?.['reason'];
  if (typeof reason === 'string') return reason;
  return '';
}

export function transcriptRole(
  row: SessionMessageDTO | RunEventDTO,
): 'user' | 'assistant' | 'system' {
  const direct = (row as SessionMessageDTO).role;
  if (direct) return direct;
  const payload = row.payload as Record<string, unknown> | undefined;
  const role = payload?.['role'];
  return role === 'user' || role === 'assistant' || role === 'system' ? role : 'system';
}

/** Best-effort tool id for a `tool_call` / `tool_result` event. */
export function eventToolId(row: RunEventDTO | SessionMessageDTO): string | null {
  const payload = row.payload as Record<string, unknown> | undefined;
  const toolId = payload?.['toolId'];
  return typeof toolId === 'string' ? toolId : null;
}
